import {useDispatch, useSelector} from "react-redux";
import {AppDispatch} from "~/store";
import {Box, Button, Card, CardBody, CardHeader, Heading, SimpleGrid, Text, useToast, VStack} from "@chakra-ui/react";
import {cancelBilling, checkBillingStatus, createBilling} from "~/store/billingSlice.ts";
import useAuthentication from "~/hooks/useAuthentication.ts";
import {Plan} from "~/types/billing.ts";
import {useEffect} from "react";

const Billing = () => {
    const dispatch = useDispatch<AppDispatch>();
    const toast = useToast();
    const {getShopifySession} = useAuthentication();
    const session = getShopifySession();
    const shop = session?.shop || '';
    const { availablePlans, loading, currentPlan } = useSelector((state: any) => state.billing);

    useEffect(() => {
        if(!availablePlans.length) {
            dispatch(checkBillingStatus(shop));
        }
    }, [dispatch, shop]);

    const handleCancel = async () => {
        try {
            await dispatch(cancelBilling(shop)).unwrap();
            toast({
                title: 'Success',
                description: 'Successfully cancelled billing',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to cancel billing',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleSelectPlan = async (planId: number) => {
        try {
            const response = await dispatch(createBilling({ shop, planId })).unwrap();
            // Redirect to Shopify billing page
            window.top!.location.href = response.confirmation_url;
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to create billing charge',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };
    
    return (
        <>
            {(currentPlan && currentPlan.status === 'active') ? (
                <Box p={8}>
                    <Card>
                        <CardHeader>
                            <Heading size="md">Current Plan</Heading>
                        </CardHeader>
                        <CardBody>
                            <VStack spacing={4} align="stretch">
                                <Text>Status: {currentPlan.status}</Text>
                                <Button
                                    colorScheme="red"
                                    isLoading={loading}
                                    onClick={handleCancel}
                                >
                                    Cancel Plan
                                </Button>
                            </VStack>
                        </CardBody>
                    </Card>
                </Box>
            ) : (
                <Box p={8}>
                    <Heading mb={6}>Choose a Plan</Heading>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10}>
                        {availablePlans.map((plan: Plan) => (
                            <Card key={plan.id}>
                                <CardHeader>
                                    <Heading size="md">{plan.name}</Heading>
                                </CardHeader>
                                <CardBody>
                                    <VStack spacing={4} align="stretch">
                                        <Text>{plan.description}</Text>
                                        <Text fontSize="2xl" fontWeight="bold">
                                            ${plan.price}
                                        </Text>
                                        {plan.trial_days > 0 && (
                                            <Text color="green.500">
                                                {plan.trial_days} days free trial
                                            </Text>
                                        )}
                                        {plan.is_credit_based && (
                                            <Text>Includes {plan.credits} credits</Text>
                                        )}
                                        <Button
                                            colorScheme="blue"
                                            isLoading={loading}
                                            onClick={() => handleSelectPlan(plan.id)}
                                        >
                                            Select Plan
                                        </Button>
                                    </VStack>
                                </CardBody>
                            </Card>
                        ))}
                    </SimpleGrid>
                </Box>
            )}
        </>
    )
}

export default Billing;